import React, { useState, useEffect, useCallback } from 'react';
import '../../styles/components/ImageSlideShow.css';

/**
 * A reusable image slideshow component.
 *
 * @param {object} props
 * @param {string[]} props.images - An array of image source URLs.
 * @param {number} [props.startIndex=0] - The index of the image to display first.
 * @param {function} props.onClose - A function to call when the slideshow should be closed.
 */
const ImageSlideshow = ({ images, startIndex = 0, onClose }) => {
    const [currentIndex, setCurrentIndex] = useState(startIndex);

    // Memoize navigation functions to prevent unnecessary re-renders and effect re-runs
    const goToPrevious = useCallback(() => {
        const isFirstSlide = currentIndex === 0;
        const newIndex = isFirstSlide ? images.length - 1 : currentIndex - 1;
        setCurrentIndex(newIndex);
    }, [currentIndex, images]);

    const goToNext = useCallback(() => {
        const isLastSlide = currentIndex === images.length - 1;
        const newIndex = isLastSlide ? 0 : currentIndex + 1;
        setCurrentIndex(newIndex);
    }, [currentIndex, images]);

    // Effect for keyboard navigation
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'ArrowLeft') {
                goToPrevious();
            } else if (event.key === 'ArrowRight') {
                goToNext();
            } else if (event.key === 'Escape') {
                onClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        // Cleanup event listener on component unmount
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [goToPrevious, goToNext, onClose]);

    // Don't render if there are no images
    if (!images || images.length === 0) {
        return null;
    }

    return (
        <div className="slideshow-container">

            <div className="slideshow-arrow left" onClick={goToPrevious}>
                &#10094;
            </div>

            <div className="slideshow-image-wrapper">
                <img
                    src={images[currentIndex]}
                    alt={`Slide ${currentIndex + 1}`}
                    className="slideshow-image"
                />
            </div>

            <div className="slideshow-arrow right" onClick={goToNext}>
                &#10095;
            </div>

            <div className="slideshow-counter">
                {currentIndex + 1} / {images.length}
            </div>
        </div>
    );
};

export default ImageSlideshow;